<template>
  <JoszakiModal
    :open="isOpen"
    :title="$t('login.noPassword.title')"
    size="sm"
    hide-footer
    @close="$emit('close')"
  >
    <template #body>
      <p>{{ $t(`login.noPassword.${variant}.description`) }}</p>
      <JoszakiButton
        v-if="variant === 'emailSent' && inboxUrl"
        expanded
        :href="inboxUrl"
        target="_blank"
        rel="noopener noreferrer"
      >
        {{ $t("login.noPassword.emailSent.goToInbox") }}
      </JoszakiButton>
      <JoszakiButton
        v-if="variant === 'requestRecovery'"
        expanded
        :loading="submitting"
        @click="requestNewPassword"
      >
        {{ $t("login.noPassword.requestRecovery.requestNewPassword") }}
      </JoszakiButton>
      <GoogleLoginButton
        :navigate-on-success="navigateOnSuccess"
        :label="$t('login.noPassword.googleLogin')"
        @success="$emit('success')"
      />
    </template>
  </JoszakiModal>
</template>

<script>
import { computed, ref, useContext } from "@nuxtjs/composition-api";
import { useSendPasswordRecovery } from "../auth";
import GoogleLoginButton from "./GoogleLoginButton.vue";

export default {
  name: "NoPasswordModal",
  components: {
    GoogleLoginButton,
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    email: {
      type: String,
      required: true,
    },
    /**
     * "emailSent": opened after a failed login attempt — the backend has just
     * sent the set-password email, so point the user at their inbox.
     * "requestRecovery": opened proactively via the noPassword=true redirect
     * from the specify-tender flow — no email was sent yet, so offer a button
     * that sends a password recovery email.
     */
    variant: {
      type: String,
      default: "emailSent",
      validator: (val) => ["emailSent", "requestRecovery"].includes(val),
    },
    /**
     * When false, the user stays on the current page after a successful Google
     * login and a `success` event is emitted instead of navigating. Used for
     * dialog login.
     */
    navigateOnSuccess: {
      type: Boolean,
      default: true,
    },
  },
  setup(props, { emit }) {
    const { $logger } = useContext();
    const submitting = ref(false);
    const sendPasswordRecovery = useSendPasswordRecovery();

    const inboxUrl = computed(() => {
      const domain = props.email.split("@")[1]?.trim().toLowerCase();
      return domain ? `https://${domain}` : null;
    });

    async function requestNewPassword() {
      try {
        submitting.value = true;
        await sendPasswordRecovery({
          email: props.email.toLowerCase().trim(),
        });
        emit("close");
      } catch (e) {
        $logger.error(e);
      } finally {
        submitting.value = false;
      }
    }

    return {
      inboxUrl,
      submitting,
      requestNewPassword,
    };
  },
};
</script>
